import React, { useState, useEffect, useMemo } from 'react';
import { auth, db } from '../../config/firebase';
import { collection, query, where, getDocs, doc, getDoc, writeBatch } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { H1, H2 } from '../../components/typography';
import { useToast } from '../../components/ui/ToastProvider';
import { Upload, X, Download, Edit, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import * as XLSX from 'xlsx';
import EditApartmentModal from '../../components/modals/EditApartmentModal';
import { hasAnyIdentity, memberDisplayName } from '../../utils/members';
import { buildApartmentKey, buildNormalizedFields, isEmptyApartmentKey } from '../../utils/apartmentKey';
import { mergeActivatedMembers, withoutEmptyDuplicates } from '../../utils/memberActivation';

export default function Units() {
  const [units, setUnits] = useState([]);
  const [unmatchedActivated, setUnmatchedActivated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentBrf, setCurrentBrf] = useState('');
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedApartment, setSelectedApartment] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [associationId, setAssociationId] = useState(null);
  const [deleteMemberModal, setDeleteMemberModal] = useState({ open: false, apartment: null });
  const [deletingMember, setDeletingMember] = useState(false);
  const toast = useToast();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        await fetchUnitsForUser(user.uid);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchUnitsForUser = async (uid) => {
    try {
      setLoading(true);
      
      // Hämta användardata för associationId
      const userRef = doc(db, 'users', uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        toast({ type: 'error', message: 'Kunde inte hitta användarinfo' });
        return;
      }
      const userData = userSnap.data();
      
      if (!userData.associationId) {
        toast({ type: 'error', message: 'Ingen förening kopplad till användaren' });
        return;
      }

      // Hämta lägenheter för denna förening
      const q = query(
        collection(db, 'housing'),
        where('associationId', '==', userData.associationId)
      );
      
      const querySnapshot = await getDocs(q);
      const fetchedUnits = [];
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        const base = {
          id: doc.id,
          apartmentNumber: data.apartmentNumber || '',
          address: data.address || '',
          city: data.city || '',
          zipCode: data.zipCode || '',
          ownedBy: data.ownedBy || '',
          members: data.members || [],
          hasMemberInfo: data.hasMemberInfo || false,
          createdAt: data.createdAt || '',
          updatedAt: data.updatedAt || ''
        };
        const members = data.members || [];
        if (members.length === 0) {
          // En lägenhet utan medlem är en egen radtyp, inte en fejkad medlem.
          // memberIndex 0 gör att "Lägg till medlem" skriver members[0].
          fetchedUnits.push({ ...base, memberIndex: 0, member: {}, empty: true });
        } else {
          members.forEach((m, i) => {
            fetchedUnits.push({ ...base, memberIndex: i, member: m });
          });
        }
      });

      // Profiler som aktiverat sig via hyria.se
      const profilesSnap = await getDocs(query(collection(db, 'users'), where('associationId', '==', userData.associationId)));
      const profiles = profilesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const merged = mergeActivatedMembers(fetchedUnits, profiles);
      setUnmatchedActivated(merged.unmatched);
      // Tom dubblettpost för en lägenhet som har en medlem visas inte (Trello 432).
      setUnits(withoutEmptyDuplicates(merged.rows));
      setCurrentBrf(userData.housingCooperative || 'BRF Admin');
      setAssociationId(userData.associationId);
      
    } catch (err) {
      toast({ type: 'error', message: 'Kunde inte hämta medlemsförteckning' });
    } finally {
      setLoading(false);
    }
  }

  // Cascading delete via Cloud Function — tar bort medlemmen från
  // housing.members[], rensar ev. ownedBy/claimedBy om samma person,
  // och raderar applications på samma lägenhet kopplade till personen.
  const handleDeleteMember = async () => {
    const apartment = deleteMemberModal.apartment;
    if (!apartment) return;
    setDeletingMember(true);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'deleteHousingMember');
      const res = await fn({
        housingId: apartment.id,
        memberIndex: apartment.memberIndex,
      });
      const counts = res?.data?.deletedCounts || {};
      const parts = ['medlemmen borttagen ur förteckningen'];
      if (counts.applications) parts.push(`${counts.applications} ansökan${counts.applications !== 1 ? 'ar' : ''} arkiverad${counts.applications !== 1 ? 'e' : ''}`);
      if (counts.ownerCleared) parts.push('ägarkopplingen rensad');
      if (counts.claimCleared) parts.push('anspråket rensat');
      toast({ type: 'success', message: `Klart: ${parts.join(', ')}.` });
      await fetchUnitsForUser(auth.currentUser?.uid);
    } catch (err) {
      toast({ type: 'error', message: `Kunde inte ta bort medlem: ${err.message}` });
    } finally {
      setDeletingMember(false);
      setDeleteMemberModal({ open: false, apartment: null });
    }
  };

  // Download template
  const downloadTemplate = () => {
    const templateData = [
      [
        'Förnamn', 
        'Efternamn', 
        'Juridisk person', 
        'Lgh nr', 
        'Gatuadress', 
        'Postnummer', 
        'Ort', 
        'Objektnr frivilligt'
      ],
      [
        'Anna', 
        'Andersson', 
        '', 
        '1001', 
        'Lindhagensgatan 81', 
        '11251', 
        'Stockholm', 
        ''
      ],
      [
        '', 
        '', 
        'Bollebo Bostads AB', 
        '4001', 
        'Lindhagensgatan 81', 
        '11251', 
        'Stockholm', 
        ''
      ]
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(templateData);
    
    // Style the header row
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const address = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!ws[address]) continue;
      ws[address].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4A90E2" } },
        alignment: { horizontal: "center", vertical: "center" }
      };
    }
    
    // Set column widths
    ws['!cols'] = [
      { wch: 12 }, // Förnamn
      { wch: 12 }, // Efternamn
      { wch: 20 }, // Juridisk person
      { wch: 8 },  // Lgh nr
      { wch: 20 }, // Gatuadress
      { wch: 10 }, // Postnummer
      { wch: 12 }, // Ort
      { wch: 15 }  // Objektnr frivilligt
    ];
    
    XLSX.utils.book_append_sheet(wb, ws, 'Medlemsförteckning');
    XLSX.writeFile(wb, 'medlemsförteckning-mall.xlsx');
    toast({ type: 'success', message: 'Mall nedladdad!' });
  };

  // Handle file upload
  const handleFileUpload = (file) => {
    if (!file) return;
    
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel' // .xls
    ];
    
    if (!validTypes.includes(file.type)) {
      toast({ type: 'error', message: 'Endast Excel (.xlsx) filer är tillåtna' });
      return;
    }
    
    setUploadedFile(file);
    parseFile(file);
  };

  // Parse Excel file
  const parseFile = async (file) => {
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { 
        type: 'array',
        codepage: 65001 // UTF-8
      });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(firstSheet, { 
        header: 1,
        defval: '',
        blankrows: false
      });
      
      if (jsonData.length < 2) {
        toast({ type: 'error', message: 'Filen måste innehålla minst en header-rad och en datarad' });
        return;
      }
      
      // Find the first row with actual data (not empty)
      let headerRowIndex = 0;
      for (let i = 0; i < jsonData.length; i++) {
        const row = jsonData[i];
        if (row && row.some(cell => cell && cell.toString().trim().length > 0)) {
          headerRowIndex = i;
          break;
        }
      }
      
      // Clean data starting from the header row
      const cleanedData = jsonData
        .slice(headerRowIndex)
        .filter(row => row && row.some(cell => cell && cell.toString().trim().length > 0))
        .map(row => row.map(cell => cell ? cell.toString().trim() : ''));
      
      if (cleanedData.length < 2) {
        toast({ type: 'error', message: 'Kunde inte hitta header-rad och datarader i filen' });
        return;
      }
      
      // Use all data for import (not just first 10 rows)
      setPreviewData(cleanedData);
      toast({ type: 'success', message: `Fil uppladdad! ${cleanedData.length - 1} rader hittades` });
      
    } catch (error) {
      toast({ type: 'error', message: 'Kunde inte läsa filen. Kontrollera att formatet är korrekt.' });
    }
  };

  // Validate row
  const validateRow = (row, headers) => {
    const errors = [];
    
    // Find column indexes
    const getColumnIndex = (columnName) => {
      const variants = {
        'Förnamn': ['Förnamn', 'förnamn', 'First Name', 'firstname'],
        'Efternamn': ['Efternamn', 'efternamn', 'Last Name', 'lastname'],
        'Juridisk person': ['Juridisk person', 'juridisk person', 'Legal Entity', 'legal entity'],
        'Lgh nr': ['Lgh nr', 'lgh nr', 'Lägenhetsnummer', 'lägenhetsnummer', 'Apartment'],
        'Gatuadress': ['Gatuadress', 'gatuadress', 'Adress', 'adress', 'Address'],
        'Postnummer': ['Postnummer', 'postnummer', 'Zip', 'zipcode'],
        'Ort': ['Ort', 'ort', 'Stad', 'stad', 'City']
      };
      
      const possibleNames = variants[columnName] || [columnName];
      return headers.findIndex(header => 
        possibleNames.some(variant => 
          header?.toLowerCase().includes(variant.toLowerCase())
        )
      );
    };

    const columnIndexes = {
      firstName: getColumnIndex('Förnamn'),
      lastName: getColumnIndex('Efternamn'),
      legalEntity: getColumnIndex('Juridisk person'),
      apartmentNumber: getColumnIndex('Lgh nr'),
      address: getColumnIndex('Gatuadress'),
      zipCode: getColumnIndex('Postnummer'),
      city: getColumnIndex('Ort')
    };

    // Check required fields
    if (columnIndexes.apartmentNumber === -1) errors.push('Lgh nr saknas');
    if (columnIndexes.address === -1) errors.push('Gatuadress saknas');
    if (columnIndexes.zipCode === -1) errors.push('Postnummer saknas');
    if (columnIndexes.city === -1) errors.push('Ort saknas');

    // Check that either (firstName + lastName) OR legalEntity exists
    const hasPersonalName = columnIndexes.firstName !== -1 && columnIndexes.lastName !== -1;
    const hasLegalEntity = columnIndexes.legalEntity !== -1;
    
    if (!hasPersonalName && !hasLegalEntity) {
      errors.push('Minst ett av följande måste finnas: (Förnamn + Efternamn) eller Juridisk person');
    }

    return { errors, columnIndexes };
  };

  // Handle import
  const handleImport = async () => {
    if (!previewData || previewData.length < 2) {
      toast({ type: 'error', message: 'Ingen data att importera' });
      return;
    }

    setImporting(true);
    
    try {
      const headers = previewData[0];
      const dataRows = previewData.slice(1);
      
      // Validate headers
      const headerValidation = validateRow([], headers);
      
      if (headerValidation.errors.length > 0) {
        toast({ 
          type: 'error', 
          message: `Saknar obligatoriska kolumner: ${headerValidation.errors.join(', ')}` 
        });
        setImporting(false);
        return;
      }

      // Get user data for associationId
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        toast({ type: 'error', message: 'Kunde inte hitta användarinfo' });
        setImporting(false);
        return;
      }
      const userData = userSnap.data();
      
      if (!userData.associationId) {
        toast({ type: 'error', message: 'Ingen förening kopplad till användaren' });
        setImporting(false);
        return;
      }

      // Läs ALLA housing-dokument, inte bara föreningens. Ansökningsflödet
      // skapar dokument utan associationId, och de var osynliga för importen —
      // som därför skapade ett andra dokument för en lägenhet som redan fanns.
      // Vi tar med föreningens egna och de föräldralösa; andra föreningars
      // lägenheter lämnas ifred även om adressen råkar likna.
      const existingHousingSnapshot = await getDocs(collection(db, 'housing'));

      // Bygg Map: lägenhetsnyckel (adress+lgh+postnr) → docRef + data + pendingMembers
      const existingHousingMap = new Map();

      existingHousingSnapshot.forEach(docSnap => {
        const data = docSnap.data();
        const belongsHere = !data.associationId || data.associationId === userData.associationId;
        if (!belongsHere) return;

        const aptKey = buildApartmentKey(data);
        if (isEmptyApartmentKey(aptKey)) return;

        // Föreningens eget dokument vinner över ett föräldralöst med samma nyckel.
        const claimed = existingHousingMap.get(aptKey);
        if (claimed && !data.associationId) return;

        existingHousingMap.set(aptKey, { ref: docSnap.ref, data, pendingMembers: [], isExisting: true });
      });

      // Process rows
      const batch = writeBatch(db);
      const results = {
        successful: 0,
        failed: 0,
        duplicate: 0,
        updated: 0,
        errors: []
      };

      for (let i = 0; i < dataRows.length; i++) {
        const row = dataRows[i];
        
        try {
          const memberData = {
            firstName: row[headerValidation.columnIndexes.firstName] || '',
            lastName: row[headerValidation.columnIndexes.lastName] || '',
            legalEntity: row[headerValidation.columnIndexes.legalEntity] || '',
            apartmentNumber: row[headerValidation.columnIndexes.apartmentNumber] || '',
            address: row[headerValidation.columnIndexes.address] || '',
            zipCode: row[headerValidation.columnIndexes.zipCode] || '',
            city: row[headerValidation.columnIndexes.city] || ''
          };

          // Validate row data
          const rowErrors = [];
          if (!memberData.apartmentNumber) rowErrors.push('Lgh nr saknas');
          if (!memberData.address) rowErrors.push('Gatuadress saknas');
          if (!memberData.zipCode) rowErrors.push('Postnummer saknas');
          if (!memberData.city) rowErrors.push('Ort saknas');

          const hasPersonalName = memberData.firstName && memberData.lastName;
          const hasLegalEntity = memberData.legalEntity;
          
          if (!hasPersonalName && !hasLegalEntity) {
            rowErrors.push('Minst ett av följande måste finnas: (Förnamn + Efternamn) eller Juridisk person');
          }

          if (rowErrors.length > 0) {
            results.failed++;
            results.errors.push({
              row: i + 2,
              name: memberData.firstName || memberData.lastName || memberData.legalEntity || 'Okänd',
              errors: rowErrors
            });
            continue;
          }

          // Dubblettkontroll på fysisk lägenhet. Nyckeln räknas ut med samma
          // funktion på båda sidor (utils/apartmentKey) — tidigare byggde
          // importen sin egen variant och missade allt som kommit in via
          // ansökan eller medlemsredigeringen.
          const aptKey = buildApartmentKey(memberData);

          const newMember = {
            firstName: (memberData.firstName || '').trim(),
            lastName: (memberData.lastName || '').trim(),
            legalEntity: (memberData.legalEntity || '').trim(),
            email: '',
            phone: ''
          };

          if (!isEmptyApartmentKey(aptKey) && existingHousingMap.has(aptKey)) {
            // Lägenhet finns redan — ackumulera medlem
            const existing = existingHousingMap.get(aptKey);
            existing.pendingMembers.push(newMember);
            results.updated++;
          } else {
            // Ny lägenhet
            const newHousingRef = doc(collection(db, 'housing'));
            const entry = {
              ref: newHousingRef,
              data: memberData,
              pendingMembers: [newMember]
            };

            // Lägg till i Map för att hantera dubbletter inom samma import
            if (!isEmptyApartmentKey(aptKey)) {
              existingHousingMap.set(aptKey, entry);
            }
            results.successful++;
          }
          
        } catch (error) {
          results.failed++;
          results.errors.push({
            row: i + 2,
            name: 'Okänd',
            errors: [`Systemfel: ${error.message}`]
          });
        }
      }

      // Bygg batch-operationer från ackumulerade members
      const commitNow = new Date().toISOString();
      for (const [, entry] of existingHousingMap) {
        if (entry.pendingMembers.length === 0) continue;

        // Vid import ersätts members helt (inte merge med befintliga)
        const allMembers = entry.pendingMembers;

        if (entry.isExisting) {
          // Befintligt doc — uppdatera. Ett föräldralöst dokument adopteras av
          // föreningen på köpet; en lägenhet som redan har en förening rörs inte.
          // update (inte set) så ownedBy och anspråksfält överlever.
          batch.update(entry.ref, {
            members: allMembers,
            hasMemberInfo: allMembers.length > 0,
            ...buildNormalizedFields(entry.data),
            ...(entry.data?.associationId
              ? {}
              : { associationId: userData.associationId, housingCooperative: currentBrf }),
            updatedAt: commitNow
          });
        } else {
          // Nytt doc
          const { normalizedAddress, normalizedApartmentNumber, normalizedZipCode } =
            buildNormalizedFields(entry.data);

          batch.set(entry.ref, {
            apartmentNumber: entry.data.apartmentNumber,
            address: entry.data.address,
            city: entry.data.city,
            zipCode: entry.data.zipCode,
            housingCooperative: currentBrf,
            associationId: userData.associationId,
            members: allMembers,
            hasMemberInfo: allMembers.length > 0,
            createdAt: commitNow,
            updatedAt: commitNow,
            ownedBy: null,
            normalizedAddress,
            normalizedApartmentNumber,
            normalizedZipCode
          });
        }
      }

      // Commit batch
      if (results.successful > 0 || results.updated > 0) {
        await batch.commit();
        await fetchUnitsForUser(auth.currentUser.uid);
      }

      // Show results
      let message = '';
      if (results.successful > 0 || results.updated > 0) {
        message = '';
        if (results.successful > 0) {
          message = `🎉 ${results.successful} nya medlemmar importerade`;
        }
        if (results.updated > 0) {
          if (message) message += ', ';
          message += `✏️ ${results.updated} medlemmar uppdaterade`;
        }
        if (results.duplicate > 0) {
          message += `, ${results.duplicate} oförändrade`;
        }
        if (results.failed > 0) {
          message += `, ${results.failed} fel`;
        }

        if (results.failed === 0 && results.duplicate === 0) {
          toast({ type: 'success', message });
          setImportModalOpen(false);
          resetImport();
        } else {
          toast({ type: 'warning', message });
        }
      } else {
        if (results.duplicate > 0 && results.failed === 0) {
          toast({
            type: 'warning',
            message: `Alla ${results.duplicate} rader var oförändrade`
          });
        } else {
          toast({
            type: 'error',
            message: `Import misslyckades: ${results.failed} fel`
          });
        }
      }
      
    } catch (error) {
      toast({ type: 'error', message: `Import misslyckades: ${error.message}` });
    } finally {
      setImporting(false);
    }
  };

  // Reset import state
  const resetImport = () => {
    setUploadedFile(null);
    setPreviewData([]);
  };

  // Räkneverk: unika lägenheter (housing-docs) vs registrerade medlemmar.
  // units är en flat list med en rad per medlem (eller en "Ej registrerad"-fallback
  // för housing-docs utan medlemmar — den ska INTE räknas som medlem).
  const uniqueApartmentCount = useMemo(
    () => new Set(units.map(u => u.id)).size,
    [units]
  );
  const registeredMemberCount = useMemo(
    () => units.filter(u => {
      const m = u.member || {};
      return Boolean(m.firstName || m.lastName || m.legalEntity);
    }).length,
    [units]
  );

  // Filtrera medlemmar baserat på sökterm
  const filteredUnits = units.filter(unit => {
    if (!searchTerm.trim()) return true;
    
    const search = searchTerm.toLowerCase();
    const m = unit.member || {};
    const memberName = memberDisplayName(m).toLowerCase();
    const legalEntity = (m.legalEntity || '').toLowerCase();
    const email = (m.email || '').toLowerCase();
    const phone = (m.phone || '').toLowerCase();
    const apartmentNumber = (unit.apartmentNumber || '').toLowerCase();
    const address = (unit.address || '').toLowerCase();
    const activatedText = unit.activated
      ? `${unit.activated.firstName || ''} ${unit.activated.lastName || ''} ${unit.activated.email || ''} ${unit.activated.phoneNumber || ''}`.toLowerCase()
      : '';

    return (
      activatedText.includes(search) ||
      memberName.includes(search) ||
      legalEntity.includes(search) ||
      apartmentNumber.includes(search) ||
      address.includes(search) ||
      email.includes(search) ||
      phone.includes(search)
    );
  });

  // Antal unika lägenheter = unika housing-dokument. Tabellraderna är
  // utplattade per medlem (memberIndex) och skulle dubbelräkna lägenheter
  // med flera ägare.
  const apartmentCount = new Set(units.map((u) => u.id)).size;
  const memberCount = units.filter((u) => !u.empty && hasAnyIdentity(u.member)).length;

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8">
      <H1>Medlemsförteckning</H1>
      <H2>{currentBrf}</H2>
      {!loading && units.length > 0 && (
        <p className="text-sm text-zinc-500 mt-1 mb-4">
          {apartmentCount} lägenheter · {memberCount} medlemmar
        </p>
      )}

      {/* Sökfält */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <input
            type="text"
            placeholder="Sök medlem (namn, lägenhet, adress, e-post...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-zinc-200 rounded-lg focus:ring-2 focus:ring-[#62B8E9] focus:border-transparent outline-none transition-all"
          />
          <svg 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-400" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        {searchTerm && (
          <div className="mt-2 text-sm text-zinc-600">
            Visar {filteredUnits.length} av {units.length} medlemmar
          </div>
        )}
      </div>
      
      {/* Action buttons */}
      <div className="flex justify-end mb-4 gap-2">
        <button
          onClick={() => setAddModalOpen(true)}
          className="bg-[#62B8E9] hover:bg-[#5ba3d4] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-sm hover:shadow-md"
        >
          <Plus className="w-4 h-4" />
          Lägg till medlem
        </button>
        <button
          onClick={() => setImportModalOpen(true)}
          className="bg-[#62B8E9] hover:bg-[#5ba3d4] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-sm hover:shadow-md"
        >
          <Upload className="w-4 h-4" />
          Importera från Excel
        </button>
        
        {/* Development only: Clear database button */}
        {process.env.NODE_ENV === 'development' && (
          <button
            onClick={async () => {
              if (!window.confirm('Är du säker på att du vill rensa hela housing databasen? Detta går inte att ångra!')) {
                return;
              }

              try {
                // Get all housing documents for current association
                const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
                const userData = userDoc.data();
                const associationId = userData?.associationId;

                if (!associationId) {
                  toast({ type: 'error', message: 'Kunde inte hitta förening för användaren' });
                  return;
                }

                const housingQuery = query(
                  collection(db, 'housing'),
                  where('associationId', '==', associationId)
                );
                const housingSnapshot = await getDocs(housingQuery);

                if (housingSnapshot.empty) {
                  toast({ type: 'info', message: 'Inga lägenheter att rensa' });
                  return;
                }

                // Delete all housing documents in batch
                const batch = writeBatch(db);
                housingSnapshot.forEach(doc => {
                  batch.delete(doc.ref);
                });

                await batch.commit();
                
                toast({ type: 'success', message: `${housingSnapshot.size} lägenheter borttagna` });
                
                // Refresh the units list
                await fetchUnitsForUser(auth.currentUser.uid);
                
              } catch (error) {
                toast({ type: 'error', message: `Fel vid rensning: ${error.message}` });
              }
            }}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-sm hover:shadow-md"
          >
            <X className="w-4 h-4" />
            Rensa databas
          </button>
        )}
      </div>
      
      {loading ? (
        <div className="bg-white rounded-2xl shadow p-6 animate-pulse mt-6">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-zinc-500">
                  <th className="px-4 py-3 text-left font-medium">Adress</th>
                  <th className="px-4 py-3 text-left font-medium">Lägenhetsnummer</th>
                  <th className="px-4 py-3 text-left font-medium">Ägare</th>
                  <th className="px-4 py-3 text-left font-medium">Telefon</th>
                  <th className="px-4 py-3 text-left font-medium">Mail</th>
                </tr>
              </thead>
              <tbody>
                {[...Array(5)].map((_, i) => (
                  <tr key={i} className="border-t border-zinc-100">
                    <td className="px-4 py-3">
                      <div className="h-4 bg-zinc-200 rounded w-32"></div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 bg-zinc-200 rounded w-16"></div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 bg-zinc-200 rounded w-24"></div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 bg-zinc-200 rounded w-20"></div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 bg-zinc-200 rounded w-28"></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow p-6 mt-6">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-zinc-500">
                  <th className="px-4 py-3 text-left font-medium">Adress</th>
                  <th className="px-4 py-3 text-left font-medium">Lägenhetsnummer</th>
                  <th className="px-4 py-3 text-left font-medium">Ägare</th>
                  <th className="px-4 py-3 text-left font-medium">Telefon</th>
                  <th className="px-4 py-3 text-left font-medium">Mail</th>
                  <th className="px-4 py-3 text-left font-medium">Åtgärder</th>
                </tr>
              </thead>
              <tbody>
                {filteredUnits.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-zinc-500">
                      {searchTerm ? 'Inga medlemmar matchar din sökning' : 'Ingen medlemsförteckning hittad'}
                    </td>
                  </tr>
                ) : (
                  filteredUnits.map((unit, idx) => (
                    <tr
                      key={`${unit.id}-${unit.memberIndex}`}
                      className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer transition-colors"
                      onClick={() => {
                        setSelectedApartment(unit);
                        setEditModalOpen(true);
                      }}
                    >
                      <td className="px-4 py-3 text-zinc-700">
                        {unit.address}
                      </td>
                      <td className="px-4 py-3 text-zinc-700 font-medium">
                        {unit.apartmentNumber ? (
                          unit.apartmentNumber
                        ) : (
                          // Utan lägenhetsnummer kan lägenheten inte matchas mot
                          // folkbokföringen — den hamnar som "kunde inte kontrolleras"
                          // i avvikelserapporten. Flagga vid källan i stället.
                          <span
                            className="inline-flex items-center gap-1 text-amber-700"
                            title="Lägenhetsnummer saknas — lägenheten kan inte kontrolleras i avvikelserapporten"
                          >
                            <AlertTriangle className="w-4 h-4" />
                            <span className="text-xs font-normal">Saknas</span>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-zinc-700">
                        {unit.empty && !unit.activated ? (
                          <span className="italic text-zinc-400 whitespace-nowrap">Lägenhet utan registrerad medlem</span>
                        ) : (
                          memberDisplayName(unit.member) ||
                          (unit.activated ? `${unit.activated.firstName || ''} ${unit.activated.lastName || ''}`.trim() : '') ||
                          'Ej registrerad'
                        )}
                        {unit.activated && (
                          <span
                            className="ml-2 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-[#EAF5FC] text-[#2C7BA5]"
                            title={`Aktiverad via hyria.se ${new Date(unit.activated.activation.at).toLocaleDateString('sv-SE')}`}
                          >
                            Aktiverad
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-zinc-700">
                        {unit.activated?.phoneNumber || unit.member?.phone || '-'}
                      </td>
                      <td className="px-4 py-3 text-zinc-700">
                        {unit.activated?.email || unit.member?.email || '-'}
                      </td>
                      <td className="px-4 py-3 text-zinc-700">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedApartment(unit);
                              setEditModalOpen(true);
                            }}
                            className="p-2 text-[#62B8E9] hover:bg-blue-50 rounded-lg transition-colors"
                            title="Redigera"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          {unit.empty ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedApartment(unit);
                                setEditModalOpen(true);
                              }}
                              className="px-2 py-1 text-xs font-semibold text-[#62B8E9] border border-[#62B8E9] rounded-lg hover:bg-blue-50 transition-colors"
                              title="Lägg till medlem på lägenheten"
                            >
                              Lägg till medlem
                            </button>
                          ) : hasAnyIdentity(unit.member) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteMemberModal({ open: true, apartment: unit });
                              }}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Ta bort medlem"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {units.length > 0 && (
            <div className="mt-6 text-sm text-zinc-500">
              {searchTerm ? (
                <>
                  Visar {filteredUnits.length} av {units.length} lägenhet{units.length !== 1 ? 'er' : ''}
                  {filteredUnits.length !== units.length && (
                    <button 
                      onClick={() => setSearchTerm('')}
                      className="ml-2 text-[#62B8E9] hover:underline"
                    >
                      Rensa sökning
                    </button>
                  )}
                </>
              ) : (
                `Totalt ${uniqueApartmentCount} lägenhet${uniqueApartmentCount !== 1 ? 'er' : ''} · ${registeredMemberCount} registrerad${registeredMemberCount !== 1 ? 'e' : ''} medlem${registeredMemberCount !== 1 ? 'mar' : ''}`
              )}
            </div>
          )}

          {unmatchedActivated.length > 0 && (
            <div className="mt-8">
              <h3 className="font-semibold text-zinc-800 mb-1">Aktiverade medlemmar utan matchad lägenhet</h3>
              <p className="text-sm text-zinc-500 mb-3">Personen har aktiverat sig via hyria.se men adressen matchar ingen lägenhet i förteckningen.</p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-zinc-500">
                      <th className="px-4 py-2 text-left font-medium">Namn</th>
                      <th className="px-4 py-2 text-left font-medium">Adress</th>
                      <th className="px-4 py-2 text-left font-medium">Telefon</th>
                      <th className="px-4 py-2 text-left font-medium">Mail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmatchedActivated.map(p => (
                      <tr key={p.id} className="border-t border-zinc-100">
                        <td className="px-4 py-2 text-zinc-700">{p.firstName} {p.lastName}</td>
                        <td className="px-4 py-2 text-zinc-700">{p.address}{p.apartmentNumber ? `, lgh ${p.apartmentNumber}` : ''}, {p.zipCode} {p.city}</td>
                        <td className="px-4 py-2 text-zinc-700">{p.phoneNumber || '-'}</td>
                        <td className="px-4 py-2 text-zinc-700">{p.email || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Import Modal */}
      <Dialog open={importModalOpen} onClose={() => setImportModalOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <Dialog.Title className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
                <Upload className="w-6 h-6 text-[#62B8E9]" />
                Importera medlemsförteckning
              </Dialog.Title>
              <button
                onClick={() => {
                  setImportModalOpen(false);
                  resetImport();
                }}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Step 1: Download Template */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Download className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-blue-900">Steg 1: Ladda ner mall</span>
                </div>
                <button
                  onClick={downloadTemplate}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors flex items-center gap-1"
                >
                  <Download className="w-3 h-3" />
                  Ladda ner Excel-mall
                </button>
              </div>
            </div>

            {/* Step 2: Upload File */}
            <div className="mb-6 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#62B8E9] transition-colors">
              <div className="flex items-center gap-3">
                <Upload className="w-5 h-5 text-gray-400" />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 mb-1">Steg 2: Ladda upp ifylld fil</div>
                  {uploadedFile ? (
                    <div className="text-sm text-green-600">
                      ✓ {uploadedFile.name} ({previewData.length - 1} rader)
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">
                      Klicka för att välja Excel-fil (.xlsx)
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => handleFileUpload(e.target.files[0])}
                  className="hidden"
                  id="file-input"
                />
                <label
                  htmlFor="file-input"
                  className="bg-[#62B8E9] hover:bg-[#5ba3d4] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer"
                >
                  {uploadedFile ? 'Välj ny' : 'Välj fil'}
                </label>
              </div>
            </div>

            {/* Preview Table */}
            {previewData.length > 0 && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                <h3 className="text-lg font-semibold text-green-800 mb-3">Steg 3: Granska data ({previewData.length - 1} rader)</h3>
                <div className="border border-green-200 rounded-xl overflow-hidden bg-white">
                  <div className="overflow-x-auto max-h-80">
                    <table className="w-full text-sm">
                      <thead className="bg-green-50">
                        <tr>
                          {previewData[0]?.map((header, index) => (
                            <th key={index} className="px-4 py-2 text-left font-medium text-green-800 border-b border-green-200">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.slice(1).map((row, rowIndex) => (
                          <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-green-25'}>
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex} className="px-4 py-2 border-b border-green-100 text-gray-800">
                                {cell || '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {importing ? (
                  <div className="mt-3 text-center">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                        <span className="text-blue-800 font-medium">Importerar medlemmar...</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 text-center">
                    <button
                      onClick={handleImport}
                      className="bg-green-600 hover:bg-green-700 text-white py-2 px-6 rounded-lg font-medium transition-colors flex items-center gap-2 mx-auto disabled:opacity-50"
                      disabled={!uploadedFile || previewData.length === 0 || importing}
                    >
                      <Upload className="w-4 h-4" />
                      Importera {previewData.length > 0 ? `${previewData.length - 1} medlemmar` : 'medlemmar'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Required Fields Info */}
            {!uploadedFile && (
              <div className="p-4 bg-gray-50 rounded-xl">
                <h4 className="font-semibold text-gray-800 mb-2">Obligatoriska fält:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                  <div>• Lgh nr</div>
                  <div>• Gatuadress</div>
                  <div>• Postnummer</div>
                  <div>• Ort</div>
                </div>
                <h4 className="font-semibold text-gray-800 mt-3 mb-2">Minst ett av följande:</h4>
                <div className="text-sm text-gray-600">
                  <div>• Förnamn + Efternamn</div>
                  <div>• Juridisk person</div>
                </div>
              </div>
            )}
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Edit Apartment Modal */}
      <EditApartmentModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedApartment(null);
        }}
        apartment={selectedApartment}
        onSuccess={() => {
          toast({ type: 'success', message: 'Lägenhet uppdaterad' });
          fetchUnitsForUser(auth.currentUser?.uid);
        }}
      />

      {/* Add Apartment Modal */}
      <EditApartmentModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        mode="add"
        associationId={associationId}
        onSuccess={() => {
          toast({ type: 'success', message: 'Medlem tillagd' });
          fetchUnitsForUser(auth.currentUser?.uid);
        }}
      />

      {/* Delete Member Confirmation Modal */}
      <Dialog
        open={deleteMemberModal.open}
        onClose={() => {
          if (!deletingMember) setDeleteMemberModal({ open: false, apartment: null });
        }}
        className="fixed z-50 inset-0 flex items-center justify-center p-4"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <Dialog.Panel className="relative bg-white rounded-2xl shadow-lg p-6 w-full max-w-md mx-auto">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Ta bort medlem?</h2>
              <p className="text-sm text-gray-600 mt-1">
                Medlemmen tas bort ur förteckningen. Hens ansökningar och historik ligger kvar, markerade som arkiverade.
              </p>
            </div>
          </div>

          {deleteMemberModal.apartment && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 text-sm">
              <div className="font-medium text-gray-900">
                {memberDisplayName(deleteMemberModal.apartment.member) || 'Okänd medlem'}
              </div>
              <div className="text-gray-600">
                Lgh {deleteMemberModal.apartment.apartmentNumber} — {deleteMemberModal.apartment.address}
              </div>
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-800">
            Inget raderas. Ansökningarna syns under &quot;Visa arkiverade&quot; i ansökningslistan, och medlemmen finns kvar i lägenhetens historik.
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setDeleteMemberModal({ open: false, apartment: null })}
              disabled={deletingMember}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Avbryt
            </button>
            <button
              onClick={handleDeleteMember}
              disabled={deletingMember}
              className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors disabled:opacity-50"
            >
              {deletingMember ? 'Tar bort...' : 'Ta bort'}
            </button>
          </div>
        </Dialog.Panel>
      </Dialog>
    </div>
  );
}